import { memo, useEffect, useState } from 'react';
import { Box, InputLabel, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseDeviceModal from '~/components/common/base-device-modal/BaseDeviceModal';
import personApi from '~/apis/person';
import { handleApiError, notify } from '~/utils/helper';

type UnassignDeviceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  personId: number;
  cccd: string;
  fullName: string;
  currentImei: string;
  onSuccess?: () => void;
};

const useUnassignDeviceApi = () => {
  const [isLoading, setIsLoading] = useState(false);

  const unassignDevice = async (personId: number, reason: string) => {
    setIsLoading(true);
    try {
      await personApi.unassignDevice(personId, { reason });
      return true;
    } catch (error) {
      handleApiError(error, true);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { unassignDevice, isLoading };
};

const UnassignDeviceModal = ({
  isOpen,
  onClose,
  personId,
  cccd,
  fullName,
  currentImei,
  onSuccess,
}: UnassignDeviceModalProps) => {
  const { t } = useTranslation('admin/common');
  const [reason, setReason] = useState('');

  const { unassignDevice, isLoading } = useUnassignDeviceApi();

  useEffect(() => {
    if (!isOpen) {
      setReason('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleConfirm = async () => {
    const reasonValue = reason.trim();

    if (!reasonValue) {
      notify('error', t('deviceModal.unassignReasonWarning'));
      return;
    }

    const isSuccess = await unassignDevice(personId, reasonValue);
    if (isSuccess) {
      notify('success', t('deviceModal.unassignSuccess'));
      onSuccess?.();
      handleClose();
    }
  };

  return (
    <BaseDeviceModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('deviceModal.unassignTitle')}
      cccd={cccd}
      fullName={fullName}
      onConfirm={handleConfirm}
      isConfirmLoading={isLoading}
      cancelText={t('cancel')}
      confirmText={t('deviceModal.unassignConfirm')}
      confirmButtonClassName='!bg-[#C62828] hover:!bg-[#b32020] !border-[#C62828]'
    >
      <Box className='flex flex-col gap-4 w-full'>
        <Box className='flex flex-col gap-2 w-full'>
          <InputLabel
            shrink={false}
            className='static !transform-none h-5 flex items-center font-medium text-sm leading-[140%] tracking-[0.02em] text-[#161A1F] mb-0'
          >
            {t('deviceModal.currentImeiLabel')}
          </InputLabel>
          <Box className='w-full h-12 rounded-[12px] px-[14px] flex items-center border-2 border-[#D7DEE7] bg-[#D7DEE7] font-normal text-sm leading-[140%] tracking-[0.02em] text-[#7B8794] select-text'>
            {currentImei || 'N/A'}
          </Box>
        </Box>

        <Box className='flex flex-col gap-2 w-full'>
          <InputLabel
            shrink={false}
            className='static !transform-none h-5 flex items-center font-medium text-sm leading-[140%] tracking-[0.02em] text-[#161A1F] mb-0'
          >
            {t('deviceModal.unassignReasonLabel')}
          </InputLabel>
          <TextField
            multiline
            fullWidth
            minRows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('deviceModal.unassignReasonPlaceholder')}
            variant='outlined'
            className='[&_.MuiOutlinedInput-root]:min-h-[96px] [&_.MuiOutlinedInput-root]:rounded-[12px] [&_.MuiOutlinedInput-root]:bg-white [&_.MuiOutlinedInput-root]:items-start [&_.MuiOutlinedInput-root]:font-normal [&_.MuiOutlinedInput-root]:text-sm [&_.MuiOutlinedInput-root]:leading-[140%] [&_.MuiOutlinedInput-root]:tracking-[0.02em] [&_.MuiOutlinedInput-root]:text-[#161A1F] [&_.MuiOutlinedInput-root]:transition-colors [&_.MuiOutlinedInput-root]:duration-200 [&_.MuiOutlinedInput-inputMultiline]:p-[14px] [&_.MuiOutlinedInput-inputMultiline]:resize-none [&_.MuiInputBase-input::placeholder]:text-[#7B8794] [&_.MuiInputBase-input::placeholder]:opacity-100 [&_.MuiOutlinedInput-notchedOutline]:border-2 [&_.MuiOutlinedInput-notchedOutline]:!border-[#D7DEE7] [&_.MuiOutlinedInput-root:hover_.MuiOutlinedInput-notchedOutline]:!border-[#005F73] [&_.MuiOutlinedInput-root.Mui-focused_.MuiOutlinedInput-notchedOutline]:!border-[#005F73] [&_.MuiOutlinedInput-root.Mui-focused:hover_.MuiOutlinedInput-notchedOutline]:!border-[#005F73]'
          />
        </Box>
      </Box>
    </BaseDeviceModal>
  );
};

export default memo(UnassignDeviceModal);
